<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useNotification } from "@/utils/notification";
import CommonButton from "@/components/common/CommonButton.vue";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import { useWorkTypeStore } from "@/features/work-types/work-type.store";
import { IWorkType } from "@/features/work-types/work-type.types";
import { Pencil02, SearchMd, Plus } from "utruckin-icons";
import CustomTab from "@/components/custom/CustomTab.vue";
import { useAppStore } from "@/store/app.store";

const workTypeStore = useWorkTypeStore();
const appStore = useAppStore();
const { pushNotification } = useNotification();

const loading = ref(false);
const workTypes = ref<IWorkType[]>([]);
const search = ref<string>("");
const editTouched = ref(false);
const activeTab = ref<number>(1);
let searchTimer: ReturnType<typeof setTimeout> | null = null;

const priorityItems = [
  { value: "LOW", name: "Low" },
  { value: "NORM", name: "Norm" },
  { value: "HIGH", name: "High" },
  { value: "URGENT", name: "Urgent" },
];

const tabs = [
  { label: "All", value: 0 },
  { label: "Active", value: 1 },
  { label: "Inactive", value: 2 },
];

// ─── ADD MODAL ───────────────────────────────────────────
const addModal = ref(false);
const addLoading = ref(false);
const addForm = ref({
  name: "",
  description: "",
  base_points: 0,
  complexity_multiplier: 1,
  default_sla_minutes: 0,
  default_priority: "",
  is_active: true,
});

const openAddModal = () => {
  addForm.value = {
    name: "",
    description: "",
    base_points: 0,
    complexity_multiplier: 1,
    default_sla_minutes: 0,
    default_priority: "",
    is_active: true,
  };
  addModal.value = true;
};

const submitAdd = async () => {
  await workTypeStore.createWorkType(
    addLoading,
    addForm.value,
    async (data: any) => {
      if (data) {
        pushNotification("Successfully saved", "success");
        workTypes.value = await workTypeStore.getWorkTypes(loading);

        addModal.value = false;
      }
    }
  );
};

// ─── EDIT MODAL ──────────────────────────────────────────
const editModal = ref(false);
const editLoading = ref(false);
const editWorkTypeId = ref<string>("");
const editForm = ref({
  description: "",
  base_points: 0,
  complexity_multiplier: 1,
  default_sla_minutes: 0,
  default_priority: "",
  is_active: true,
});

// Валидация edit формы

const isEditFormValid = computed(
  () =>
    String(editForm.value.base_points).trim() !== "" &&
    String(editForm.value.default_sla_minutes).trim() !== ""
);

const editBasePointsError = computed(
  () => editTouched.value && String(editForm.value.base_points).trim() === ""
);

const editMinutesError = computed(
  () =>
    editTouched.value &&
    String(editForm.value.default_sla_minutes).trim() === ""
);

const openEditModal = (item: IWorkType) => {
  editTouched.value = false;
  editWorkTypeId.value = item.id;
  editForm.value = {
    description: item.description,
    base_points: item.base_points,
    complexity_multiplier: item.complexity_multiplier,
    default_sla_minutes: item.default_sla_minutes,
    default_priority: item.default_priority,
    is_active: item.is_active,
  };
  editModal.value = true;
};

const submitEdit = async () => {
  editTouched.value = true;
  if (!isEditFormValid.value) return;
  const payload = {
    description: editForm.value.description,
    base_points: Number(editForm.value.base_points),
    complexity_multiplier: Number(editForm.value.complexity_multiplier),
    default_sla_minutes: Number(editForm.value.default_sla_minutes),
    default_priority: editForm.value.default_priority,
    is_active: editForm.value.is_active,
  };
  const result = await workTypeStore.updateWorkType(
    editLoading,
    payload,
    editWorkTypeId.value
  );
  if (result) {
    pushNotification("Successfully saved", "success");
    workTypes.value = await workTypeStore.getWorkTypes(loading);
    editModal.value = false;
  }
};

const getList = async () => {
  workTypes.value = await workTypeStore.getWorkTypes(loading, true);
};

onMounted(async () => {
  await getList();

  if (workTypeStore.commonFilter?.search) {
    search.value = workTypeStore.commonFilter.search;
  }
  if (workTypeStore.commonFilter?.is_active == null) {
    activeTab.value = 0;
  } else if (workTypeStore.commonFilter?.is_active == true) {
    activeTab.value = 1;
  } else {
    activeTab.value = 2;
  }
});

watch(
  () => activeTab.value,
  async (newVal) => {
    if (workTypeStore.commonFilter) {
      if (newVal == 0) {
        workTypeStore.commonFilter.is_active = null;
      } else workTypeStore.commonFilter.is_active = newVal == 1;

      await getList();
    }
  }
);

watch(
  () => search.value,
  async (val) => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(async () => {
      if (workTypeStore.commonFilter) {
        workTypeStore.commonFilter.search = val;
        await getList();
      }
    }, 400);
  }
);

watch(
  () => appStore.refreshKey,
  () => {
    getList();
  },
  {
    immediate: false,
  }
);
</script>

<template>
  <div class="v-working">
    <div class="v-working__header">
      <CommonSearch
        v-model="search"
        :icon="SearchMd"
        placeholder="Search"
        clearable
      />
      <CustomTab :tabs="tabs" v-model="activeTab" />
      <CommonButton
        type="primary"
        label="Add working"
        :prepend="true"
        :prependIcon="Plus"
        @clicked="openAddModal"
      />
    </div>

    <CommonTable
      :headers="workTypeStore.workTypeHeaders"
      :items="workTypes"
      :loading="loading"
    >
      <template #id="{ index }">
        <span>{{ index + 1 }}</span>
      </template>

      <template #title="{ item }">
        <span>{{ item.name }}</span>
      </template>

      <template #base_points="{ item }">
        <span class="v-working__badge">{{ item.base_points }}</span>
      </template>

      <template #complete_minutes="{ item }">
        <span>{{ item.default_sla_minutes + " minutes" }}</span>
      </template>

      <template #priority="{ item }">
        <span>{{ item.default_priority }}</span>
      </template>

      <template #status="{ item }">
        <div class="status" :class="item.is_active ? 'active' : 'inactive'">
          {{ item.is_active ? "active" : "inactive" }}
        </div>
      </template>

      <template #actions="{ item }">
        <div class="v-working__actions">
          <button class="v-working__btn" @click="openEditModal(item)">
            <Pencil02 :size="18" />
          </button>
          <!--          <button class="v-working__btn">-->
          <!--            <Trash01 :size="18" />-->
          <!--          </button>-->
        </div>
      </template>
    </CommonTable>

    <!-- ADD MODAL -->
    <CommonModal v-model="addModal" title="Add working">
      <div class="v-working__form">
        <CommonInput v-model="addForm.name" label="Name" />
        <CommonInput v-model="addForm.description" label="Description" />
        <CommonInput
          v-model.number="addForm.base_points"
          label="Base points"
          type="number"
        />
        <CommonInput
          v-model.number="addForm.default_sla_minutes"
          label="Complete minutes"
          type="number"
        />
        <CommonDropdown
          v-model="addForm.default_priority"
          :items="priorityItems"
          label="Priority"
        />
      </div>

      <template #footer>
        <CommonButton
          type="outline"
          label="Cancel"
          @clicked="addModal = false"
        />
        <CommonButton
          type="primary"
          label="Save"
          :loading="addLoading"
          @clicked="submitAdd"
        />
      </template>
    </CommonModal>

    <!-- EDIT MODAL -->
    <CommonModal v-model="editModal" title="Edit working">
      <div class="v-working__form">
        <CommonInput v-model="editForm.description" label="Description" />
        <CommonInput
          v-model.number="editForm.base_points"
          label="Base points"
          type="number"
          :error="editBasePointsError"
          errorMessage="Base points is required"
        />
        <CommonInput
          v-model.number="editForm.default_sla_minutes"
          label="Complete minutes"
          type="number"
          :error="editMinutesError"
          errorMessage="Complete minutes is required"
        />
        <CommonDropdown
          v-model="editForm.default_priority"
          :items="priorityItems"
          label="Priority"
        />
      </div>

      <template #footer>
        <CommonButton
          type="outline"
          label="Cancel"
          @clicked="editModal = false"
        />
        <CommonButton
          type="primary"
          label="Save"
          :loading="editLoading"
          @clicked="submitEdit"
        />
      </template>
    </CommonModal>
  </div>
</template>

<style scoped lang="scss">
.v-working {
  padding: var(--base-space-24);
  display: flex;
  flex-direction: column;
  gap: var(--base-space-16);
  height: calc(100vh - 80px);
  background: var(--color-system-background);

  &__header {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--base-space-8);
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: var(--base-space-12);
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--base-space-4) var(--base-space-8);
    border-radius: var(--base-radius-8);
    background-color: var(--color-blue-light);
    color: var(--color-label);
    font-size: 14px;
    font-weight: 600;
    min-width: 40px;
  }

  &__actions {
    display: flex;
    gap: var(--base-space-8);
  }

  &__btn {
    cursor: pointer;
    border: 1px solid var(--color-border);
    transition: opacity 0.2s;
    background-color: var(--color-bg-secondary);
    color: var(--color-label);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    padding: 6px;
    border-radius: var(--base-radius-12);

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>
